/*

Order Contact Info
Will be used to display the contact information of the user who placed the order.
This component will be used in the Checkout Page.
It will display the following information:
Email
Phone Number

*/

import React, {
  useEffect, useCallback, useRef,
} from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import PropTypes from 'prop-types';
import {
  HeadingBanner, InputField, ErrorMessage, FieldGroup, Checkbox, LegalText, Link,
} from 'anf-core-react';
import isValidEmail from '../../../tools/validations/isValidEmail';
import isValidPhone from '../../../tools/validations/isValidPhone';
import { setHashedEmailForAnalytics } from '../../../tools/utils';
import useSwitchesFromContext from '../../../hooks/useSwitchesFromContext';
import $window from '../../../tools/window';
import { useStepsContext } from '../../Steps/StepsContext';

export default function OrderContactInfo({
  orderContactData,
  userData,
  hasOnlyEGiftCard,
  isSmsOptInEnabled,
}) {
  // Remember the initial email value to prevent disabling input
  const initialEmailRef = useRef(Boolean(orderContactData.email));

  // Try to get current step context (will be null if not in stepped checkout)
  let currentStep = null;
  try {
    const stepsContext = useStepsContext();
    currentStep = stepsContext.currentStep;
  } catch (error) {
    // Steps context not available
    currentStep = null;
  }

  // Access form methods from provider
  const {
    control, setValue, watch, getValues, trigger, formState: { errors },
  } = useFormContext();

  // Use root-level fields
  const emailFieldName = 'email';
  const phoneFieldName = 'phone';
  const emailValue = watch(emailFieldName);

  const { getSwitchValue } = useSwitchesFromContext();
  const isSMSOptInEnabled = getSwitchValue('fulSmsOptIn');
  const searchParams = new URLSearchParams(window.location.search);
  const showSmsOptIn = isSMSOptInEnabled || searchParams.get('enablesms') === 'true';

  // Email validation for react-hook-form
  const validateEmailFn = useCallback(
    (
      value,
    ) => isValidEmail(value) || orderContactData?.invalidEmailLabel?.value,
    [orderContactData?.invalidEmailLabel?.value],
  );

  // Phone validation for react-hook-form
  const validatePhoneFn = useCallback(
    (
      value,
    ) => isValidPhone(value) || orderContactData?.invalidPhoneLabel?.value,
    [orderContactData?.invalidPhoneLabel?.value],
  );

  // One-time initialization of email/phone/smsOptIn and CRS listeners
  useEffect(() => {
    // Prefer sessionStorage email for guests when field is empty, else use context
    const currentEmail = getValues(emailFieldName);
    if (!currentEmail || String(currentEmail).trim() === '') {
      const guestEmail = sessionStorage?.getItem('guestCustomerMailId');
      const emailToSet = (!userData.isLoggedIn && guestEmail)
        ? guestEmail
        : orderContactData?.email;
      if (emailToSet) setValue(emailFieldName, emailToSet);
    }

    // Set phone from context when missing
    const currentPhone = getValues(phoneFieldName);
    if ((!currentPhone || String(currentPhone).trim() === '') && orderContactData?.phoneNumber) {
      setValue(phoneFieldName, orderContactData.phoneNumber);
    }

    // Initialize smsOptIn only if it's not set yet
    const currentSmsOptIn = getValues('smsOptIn');
    if (currentSmsOptIn === undefined && orderContactData?.smsOptIn !== undefined) {
      setValue('smsOptIn', orderContactData.smsOptIn);
    }

    // CRS email updates only populate when empty and guest
    const setEmailValueFromSessionStorage = () => {
      const guestEmail = sessionStorage?.getItem('guestCustomerMailId');
      const current = getValues(emailFieldName);
      if (!userData.isLoggedIn && guestEmail && (!current || String(current).trim() === '')) {
        setValue(emailFieldName, guestEmail);
      }
    };

    // This event is triggered when the CRS flow persists the user's email
    $window.addEventListener('recognizeCustomer:persist:email', setEmailValueFromSessionStorage);
    // This event is triggered when the MFE recognize customer flow continues as a guest
    $window.addEventListener('RecognizeCustomer:ContinueAsGuest', setEmailValueFromSessionStorage);

    return () => {
      $window.removeEventListener('recognizeCustomer:persist:email', setEmailValueFromSessionStorage);
      $window.removeEventListener('RecognizeCustomer:ContinueAsGuest', setEmailValueFromSessionStorage);
    };
    // Intentionally run once on mount for one-time initialization
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // New effect to hash email only when `emailValue` updates and is valid
  useEffect(() => {
    async function updateHashedEmailForAnalytics() {
      if (emailValue && isValidEmail(emailValue) && !errors[emailFieldName]) {
        await setHashedEmailForAnalytics(emailValue);
      }
    }

    updateHashedEmailForAnalytics();
  }, [emailValue, errors, emailFieldName]);

  // this useEffect is used to validate the email and phone number fields
  // it is triggered by CRS app validation that also runs through these fields
  useEffect(() => {
    function handleCartValidation() {
      trigger();
    }
    $window.addEventListener('cartModel:validationFailed', handleCartValidation);

    return () => {
      $window.removeEventListener('cartModel:validationFailed', handleCartValidation);
    };
  }, [trigger]);

  // will not render if hideContactInfo is true and it will be true only for paypalexpress flow
  // the condition is used to checked to display ordercontactinfo in other flows
  // TODO: make this check in BFF whenever url is available
  if (orderContactData?.hideContactInfo && $window.location.href.includes('ProcessPayPalReturnAndOrderExpress')) {
    return null;
  }

  return (
    <div className="order-contact-info-mfe scope-1892">
      {!currentStep && (
        <HeadingBanner
          variant="boxed"
        >
          <h2 data-prop="">{orderContactData?.headingLabel?.value}</h2>
        </HeadingBanner>
      )}
      <FieldGroup
        legend="Contact Information"
      >
        <Controller
          name={emailFieldName}
          control={control}
          rules={{
            required: true,
            validate: validateEmailFn,
          }}
          render={({ field, fieldState }) => (
            <InputField
              id="email"
              type="email"
              label={orderContactData?.emailLabel?.value}
              isInvalid={Boolean(fieldState.error)}
              isDisabled={
                userData.isLoggedIn
                && initialEmailRef.current
                && field.value?.length
                && !fieldState.error
              }
              required
              autoComplete=""
              {...field}
              value={field.value || ''}
            >
              {fieldState.error && (
                <ErrorMessage id="email-invalid-error">
                  {fieldState.error.message || orderContactData?.invalidEmailLabel?.value}
                </ErrorMessage>
              )}
            </InputField>
          )}
        />

        <Controller
          name={phoneFieldName}
          control={control}
          rules={{
            required: true,
            validate: validatePhoneFn,
          }}
          render={({ field, fieldState }) => (
            <InputField
              id="phoneNumber"
              type="tel"
              label={orderContactData?.phoneNumberLabel?.value}
              isInvalid={Boolean(fieldState.error)}
              required
              autoComplete="tel"
              {...field}
              value={field.value || ''}
            >
              {fieldState.error && (
                <ErrorMessage id="phone-invalid-error">
                  {fieldState.error.message || orderContactData?.invalidPhoneLabel?.value}
                </ErrorMessage>
              )}
            </InputField>
          )}
        />
      </FieldGroup>
      {orderContactData.addressType !== 'popins' && showSmsOptIn && !hasOnlyEGiftCard && isSmsOptInEnabled && (
      <div className="sms-section">
        <Controller
          name="smsOptIn"
          control={control}
          render={({ field }) => (
            <Checkbox
              description={orderContactData.smsOptInLabel?.value}
              id="smsOptIn-id"
              isChecked={field.value}
              onChange={(e) => field.onChange(e.currentTarget.checked)}
              value={`${field.value}`}
              name="smsOptIn"
            />
          )}
        />
        <LegalText>
          {orderContactData.smsOptInDetailedLabel?.value?.split('{0}')[0]}
          <Link
            href={orderContactData.privacyUrl}
            target="_blank"
          >
            {orderContactData.smsOptInPrivacyNotice.value}
          </Link>

          &nbsp;
          {orderContactData.smsOptInAnd.value}
          &nbsp;
          <Link
            href={orderContactData.textTermUrl}
            target="_blank"
          >
            {orderContactData.smsOptInTextTerm.value}
          </Link>
          &nbsp;
          {orderContactData.smsOptInDetailedLabel?.value?.split('{1}')[1]}
        </LegalText>
      </div>
      )}

      {/* Hidden fields for compatibility */}
      <input
        type="hidden"
        {...control.register(`addresses.${orderContactData.addressType}.email`)}
      />
      <input
        type="hidden"
        {...control.register(`addresses.${orderContactData.addressType}.phone`)}
      />
    </div>
  );
}

OrderContactInfo.propTypes = {
  orderContactData: PropTypes.shape({
    hideContactInfo: PropTypes.bool,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    smsOptIn: PropTypes.bool,
    privacyUrl: PropTypes.string,
    textTermUrl: PropTypes.string,
    smsOptInLabel: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    smsOptInDetailedLabel: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    smsOptInPrivacyNotice: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    smsOptInTextTerm: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    smsOptInAnd: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    headingLabel: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    emailLabel: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    phoneNumberLabel: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    invalidEmailLabel: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    invalidPhoneLabel: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string,
    }),
    addressType: PropTypes.string,
  }),
  userData: PropTypes.shape({
    isLoggedIn: PropTypes.bool,
  }),
  hasOnlyEGiftCard: PropTypes.bool,
  isSmsOptInEnabled: PropTypes.bool,
};

OrderContactInfo.defaultProps = {
  orderContactData: {
    hideContactInfo: false,
    email: '',
    phoneNumber: '',
    smsOptIn: false,
    privacyUrl: '',
    textTermUrl: '',
    smsOptInLabel: {
      key: '',
      value: 'Sign up to receive text notifications about your shipment',
    },
    smsOptInDetailedLabel: {
      key: '',
      value: 'Once your order has shipped, you will receive automated text messages to the phone number you provided. Message & data rates may apply. Message frequency varies. Reply STOP to opt out of these text notifications. View our {0} and {1} that apply.',
    },
    smsOptInPrivacyNotice: {
      key: 'SMSOPTIN_PRIVACY_NOTICE',
      value: 'Privacy Notice',
    },
    smsOptInTextTerm: {
      key: 'SMSOPTIN_TEXT_TERMS',
      value: 'Text Terms',
    },
    smsOptInAnd: {
      key: 'SMSOPTIN_AND',
      value: 'and',
    },
    emailLabel: {
      key: '',
      value: 'Email Address',
    },
    phoneNumberLabel: {
      key: '',
      value: 'Phone Number',
    },
    headingLabel: {
      key: '',
      value: 'Order Contact',
    },
    invalidEmailLabel: {
      key: '',
      value: 'Please enter a valid email address.',
    },
    invalidPhoneLabel: {
      key: '',
      value: 'Please enter a valid phone number.',
    },
    addressType: 'homeDelivery',
  },
  userData: {
    isLoggedIn: false,
  },
  hasOnlyEGiftCard: false,
  isSmsOptInEnabled: false,
};
